// The frozen polar chart: one Gaussian per valid texel, laid out in iris-centred rings.

import { fetchAssetArrayBuffer, fetchAssetJson } from '../assets/fetchAsset.ts'
import { matrix3, vector3 } from '../math/index.ts'
import type { Matrix3, Vector3 } from '../math/index.ts'
import type { EyeSide } from './eyeSide.ts'

export type EyeChart = {
  resolution: number
  numTexels: number
  uvGrid: Float32Array
  triangleIndices: Uint32Array
  barycentricWeights: Float32Array
  valid: Uint8Array
  /** Per-texel azimuthal step (the exporter's `dphi`). */
  azimuthalStep: Float32Array
  /** Per-Gaussian SG roughness in radians (the exporter's `spec_sigma`). */
  specularSigma: Float32Array
  /** The one meridional step for the whole chart (the exporter's `dtheta`). */
  meridionalStep: number
  poleIndex: number
}

/** Per-axis extent multipliers: the initial guess, and the hard floor/cap around it. */
export type ExtentMultipliers = { init: number, floor: number, cap: number }

/** The head pose the decoders were conditioned at — the frame `gaze_ref` lives in. */
export type HeadPose = { rotation: Matrix3, translation: Vector3 }

export type EyeMeta = {
  chartResolution: number
  uvResolution: number
  opacityBias: number
  specVisBias: number
  bsigGuard: number
  tangent: ExtentMultipliers
  normal: ExtentMultipliers
  spec: { lightDirs: Float32Array, lightIntensity: Float32Array, nLights: number, sgNorm: number }
  condGazeColumn: Record<EyeSide, number>
  headPose: HeadPose
  /** Each chart's own reference gaze, head space — what identifies which eye owns it. */
  gazeReference: Record<EyeSide, Vector3 | null>
}

const DTYPE_BYTES: Record<string, number> = { f32: 4, u32: 4, i32: 4, u8: 1 }

const REQUIRED_BUFFERS = ['uv_grid', 'idxim', 'barim', 'valid', 'dphi', 'spec_sigma']

type BufferDescriptor = { name: string, dtype: string, shape: number[], offset: number, byte_length: number }

function bufferView(blob: ArrayBuffer, descriptor: BufferDescriptor) {
  const count = descriptor.shape.reduce((total, dimension) => total * dimension, 1)
  const bytesPerValue = DTYPE_BYTES[descriptor.dtype]
  if (!bytesPerValue) {
    throw new Error(`eye statics: unsupported dtype '${descriptor.dtype}' for ${descriptor.name}`)
  }
  if (descriptor.byte_length !== count * bytesPerValue) {
    throw new Error(`eye statics: ${descriptor.name} byte_length ${descriptor.byte_length} `
      + `!= ${count * bytesPerValue}`)
  }
  if (descriptor.dtype === 'f32') return new Float32Array(blob, descriptor.offset, count)
  if (descriptor.dtype === 'u8') return new Uint8Array(blob, descriptor.offset, count)
  return new Uint32Array(blob, descriptor.offset, count)
}

// Decoding is pure so a test can drive it from bytes on disk.
export function decodeEyeChart(blob: ArrayBuffer, manifest: any, rawMeta: any,
                               side: EyeSide): EyeChart {
  const descriptors: Record<string, BufferDescriptor> = {}
  for (const descriptor of manifest.buffers) descriptors[descriptor.name] = descriptor
  const missing = REQUIRED_BUFFERS.filter((name) => !descriptors[name])
  if (missing.length) throw new Error(`eye statics ${side}: missing buffers ${missing.join(', ')}`)

  const resolution = manifest.chart_res
  const numTexels = manifest.num_texels
  const chart: EyeChart = {
    resolution,
    numTexels,
    uvGrid: bufferView(blob, descriptors.uv_grid) as Float32Array,
    triangleIndices: bufferView(blob, descriptors.idxim) as Uint32Array,
    barycentricWeights: bufferView(blob, descriptors.barim) as Float32Array,
    valid: bufferView(blob, descriptors.valid) as Uint8Array,
    azimuthalStep: bufferView(blob, descriptors.dphi) as Float32Array,
    specularSigma: bufferView(blob, descriptors.spec_sigma) as Float32Array,
    meridionalStep: rawMeta.eyes[side].dtheta,
    poleIndex: rawMeta.eyes[side].pole_idx,
  }

  // Every shape is load-bearing: a mismatched chart samples garbage and still renders.
  const perTexel = (values: ArrayLike<number>, name: string, stride = 1) => {
    if (values.length !== numTexels * stride) throw new Error(`eye statics ${side}: ${name} length`)
  }
  const perMapTexel = (values: ArrayLike<number>, name: string, stride = 1) => {
    if (values.length !== resolution * resolution * stride) {
      throw new Error(`eye statics ${side}: ${name} length`)
    }
  }
  perTexel(chart.uvGrid, 'uv_grid', 2)
  perTexel(chart.azimuthalStep, 'dphi')
  perTexel(chart.specularSigma, 'spec_sigma')
  perMapTexel(chart.triangleIndices, 'idxim', 3)
  perMapTexel(chart.barycentricWeights, 'barim', 3)
  perMapTexel(chart.valid, 'valid')
  if (!(chart.meridionalStep > 0)) throw new Error(`eye meta ${side}: dtheta missing`)
  return chart
}

// The one colour contract this runtime implements: diffuse = sigmoid(appr_rgb).
export const DIFFUSE_MODE = 'decoder'

export function decodeEyeMeta(raw: any): EyeMeta {
  const multipliers = raw?.mults
  if (!multipliers?.tangent || !multipliers?.normal) throw new Error('eye_meta: no mults')
  if (raw.diffuse !== DIFFUSE_MODE) {
    // An unmarked bundle may be the superseded residual contract, which renders mid-grey.
    throw new Error(`eye_meta: diffuse '${raw.diffuse ?? '(absent)'}' != '${DIFFUSE_MODE}'`
      + ` — re-export this bundle (aosTools/texavatars/export_eye_assets.py)`)
  }
  if (!(raw.spec?.sg_norm > 0)) throw new Error('eye_meta: spec.sg_norm missing')
  if (!(raw.bsig_guard >= 0)) throw new Error('eye_meta: bsig_guard missing')
  if (raw.chart_res !== raw.uv_res) {
    // Any other uv_res samples a map the trained branch never renders through.
    throw new Error(`eye_meta: uv_res ${raw.uv_res} != chart_res ${raw.chart_res}`)
  }
  const spec = raw.spec ?? {}
  return {
    chartResolution: raw.chart_res,
    uvResolution: raw.uv_res,
    opacityBias: raw.opacity_bias,
    specVisBias: raw.spec_vis_bias,
    bsigGuard: raw.bsig_guard,
    tangent: multipliers.tangent,
    normal: multipliers.normal,
    spec: {
      nLights: spec.n_lights ?? 0,
      lightDirs: new Float32Array((spec.light_dirs ?? []).flat()),
      lightIntensity: new Float32Array(spec.light_intensity ?? []),
      sgNorm: spec.sg_norm,
    },
    // Declared by the exporter, never inferred: Myra's gaze<->eye pairing is crossed.
    condGazeColumn: raw.cond_gaze_column ?? { left: 0, right: 3 },
    headPose: {
      rotation: matrix3.fromRows(raw.head_R, 'eye_meta.head_R'),
      translation: vector3.parse(raw.head_t, 'eye_meta.head_t'),
    },
    gazeReference: {
      left: readGazeReference(raw, 'left'),
      right: readGazeReference(raw, 'right'),
    },
  }
}

// Absent falls back to the identity chart->eye pairing, which resolveCondOwners reports.
function readGazeReference(raw: any, side: EyeSide): Vector3 | null {
  const reference = raw?.eyes?.[side]?.gaze_ref
  if (!reference) return null
  return vector3.normalize(vector3.parse(reference, `eye_meta.eyes.${side}.gaze_ref`))
}

export type EyeChartSet = { charts: Record<EyeSide, EyeChart>, meta: EyeMeta }

// Bundle path: bytes + already-parsed json, no fetching.
export function buildEyeCharts(blobs: { left: ArrayBuffer, right: ArrayBuffer },
                               manifests: { left: any, right: any },
                               rawMeta: any): EyeChartSet {
  return {
    charts: {
      left: decodeEyeChart(blobs.left, manifests.left, rawMeta, 'left'),
      right: decodeEyeChart(blobs.right, manifests.right, rawMeta, 'right'),
    },
    meta: decodeEyeMeta(rawMeta),
  }
}

export async function loadEyeCharts(urls: {
  leftBin: string, leftJson: string, rightBin: string, rightJson: string, meta: string,
}): Promise<EyeChartSet> {
  const what = 'eye statics'
  const [leftBlob, leftManifest, rightBlob, rightManifest, rawMeta] = await Promise.all([
    fetchAssetArrayBuffer(urls.leftBin, what),
    fetchAssetJson(urls.leftJson, what),
    fetchAssetArrayBuffer(urls.rightBin, what),
    fetchAssetJson(urls.rightJson, what),
    fetchAssetJson(urls.meta, what),
  ])
  return buildEyeCharts({ left: leftBlob, right: rightBlob },
    { left: leftManifest, right: rightManifest }, rawMeta)
}
